import { ErrorResponse, ErrorTimeout } from 'oidc-client-ts'

/**
 * Thrown when the API bridge reports Keycloak/backend unavailable (e.g. HTTP 502).
 * The login UI should show a generic "try again later" message, not the 2FA step.
 */
export class LoginServiceUnavailableError extends Error {
  readonly code = 'LOGIN_SERVICE_UNAVAILABLE' as const

  constructor(message = 'Login service unavailable') {
    super(message)
    this.name = 'LoginServiceUnavailableError'
  }
}

export function isLoginServiceUnavailableError(
  e: unknown
): e is LoginServiceUnavailableError {
  return e instanceof LoginServiceUnavailableError
}

/**
 * Detects network / IdP outages so we do not mislead users into the OTP (2FA) step.
 */
export function isLikelyAuthInfrastructureFailure(e: unknown): boolean {
  if (isLoginServiceUnavailableError(e)) {
    return true
  }
  if (e instanceof ErrorTimeout) {
    return true
  }
  if (e instanceof ErrorResponse) {
    const code = e.error ?? ''
    return (
      code === 'server_error' ||
      code === 'temporarily_unavailable' ||
      code === 'service_unavailable'
    )
  }
  if (e instanceof TypeError && typeof e.message === 'string') {
    return /failed to fetch|networkerror|load failed|fetch/i.test(e.message)
  }
  if (e instanceof Error && typeof e.message === 'string') {
    return /failed to fetch|network error|load failed|networkrequestfailed|err_network|connection refused|econnrefused|networkerror|aborted|timeout|502|503|504|bad gateway|service unavailable|unreachable/i.test(
      e.message
    )
  }
  return false
}
